import './marker.css';

import Popover from '@mui/material/Popover';
import { Marker, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useMemo, useState } from 'react';

import { GOOGLE_MAPS_LOADER_OPTIONS, MarkerType } from './constants';
import { withDefaultSiteRing } from './defaultSiteMarkerStyle';
import { DROPOFF_PHASE_COLOR, PICKUP_PHASE_COLOR } from './loadPhase';
import { TreadMarkerProps } from './types';

type Props = TreadMarkerProps;

const dropoffSiteSvg =
  '<svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  `<circle cx="16" cy="16" r="15" fill="${DROPOFF_PHASE_COLOR}" stroke="white" stroke-width="2"/>` +
  '<path d="M24 16L22.59 14.59L17 20.17V8H15V20.17L9.42 14.58L8 16L16 24L24 16Z" fill="white"/></svg>';

const pickupSiteSvg =
  '<svg width="24" height="24" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  `<circle cx="18" cy="18" r="17" fill="${PICKUP_PHASE_COLOR}" stroke="white" stroke-width="2"/>` +
  '<path d="M10 18L11.41 19.41L17 13.83V26H19V13.83L24.58 19.42L26 18L18 10L10 18Z" fill="white"/></svg>';

const stagingSiteSvg =
  '<svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="16" cy="16" r="15" fill="#0891B2" stroke="white" stroke-width="2"/>' +
  '<path d="M16 8L14.59 9.41L20.17 15H8V17H20.17L14.58 22.58L16 24L24 16L16 8Z" fill="white"/></svg>';

const bothSiteSvg =
  '<svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="16" cy="16" r="15" fill="#1B2480" stroke="white" stroke-width="2"/>' +
  '<path d="M20 21.01V14H18V21.01H15L19 25L23 21.01H20ZM13 7L9 10.99H12V18H14V10.99H17L13 7ZM20 21.01V14H18V21.01H15L19 25L23 21.01H20ZM13 7L9 10.99H12V18H14V10.99H17L13 7Z" fill="white"/></svg>';

const toDataUrl = (svg: string): string =>
  'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

// Data URLs for default (candidate) sites: a muted icon + same-color dashed
// ring, used instead of the solid marker when a site is a default site.
const defaultSiteUrlByType: Partial<Record<MarkerType, string>> = {
  [MarkerType.pickup_site]: toDataUrl(withDefaultSiteRing(pickupSiteSvg)),
  [MarkerType.dropoff_site]: toDataUrl(withDefaultSiteRing(dropoffSiteSvg)),
  [MarkerType.staging_site]: toDataUrl(withDefaultSiteRing(stagingSiteSvg)),
  [MarkerType.both_site]: toDataUrl(withDefaultSiteRing(bothSiteSvg)),
};

const dropoffSiteGeodebugSvg =
  '<svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="16" cy="16" r="15" fill="#ED6C02" stroke="white" stroke-width="2"/>' +
  '<path d="M24 16L22.59 14.59L17 20.17V8H15V20.17L9.42 14.58L8 16L16 24L24 16Z" fill="white"/></svg>';

const pickupSiteGeodebugSvg =
  '<svg width="24" height="24" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="18" cy="18" r="17" fill="#ED6C02" stroke="white" stroke-width="2"/>' +
  '<path d="M10 18L11.41 19.41L17 13.83V26H19V13.83L24.58 19.42L26 18L18 10L10 18Z" fill="white"/></svg>';

export function TreadMarker(marker: Props) {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [popoverVersion, setPopoverVersion] = useState<'hover' | 'click'>(
    marker.type === MarkerType.truck ? 'hover' : 'click',
  );

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const markerTypeToIcon: Record<
    MarkerType,
    google.maps.Icon | google.maps.Symbol
  > | null = useMemo(() => {
    if (!isLoaded) return null;
    return {
      [MarkerType.truck]: {
        path: 'M14 0 28 30 14 24 0 30 14 0',
        fillColor: '#006F12',
        fillOpacity: 1,
        scale: 0.6,
        strokeWeight: 2,
        strokeColor: '#ffffff',
        anchor: new window.google.maps.Point(16, 17),
        labelOrigin: new window.google.maps.Point(16, 56),
      },
      [MarkerType.site]: {
        path: 'M29,4.8 C19.6,4.8 14.4,10.8 14.4,19.2 C14.4,28.8 29,48 29,48 C29,48 43.6,28.8 43.6,19.2 C43.6,10.8 38.4,4.8 29,4.8 Z M29,24 A4.8,4.8 0 1,1 29,14.4 A4.8,4.8 0 1,1 29,24 Z',
        fillColor: '#F05039',
        fillOpacity: 1,
        strokeWeight: 2,
        scale: 0.9,
        strokeColor: '#ffffff',
        anchor: new window.google.maps.Point(30, 42),
      },
      [MarkerType.moving_site]: {
        path: 'M10,2 A8,8 0 1,0 10,18 A8,8 0 1,0 10,2',
        fillColor: 'black',
        fillOpacity: 1,
        scale: 0.9,
        strokeWeight: 2,
        strokeColor: '#ffffff',
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.ping]: {
        path: 'M10,2 A8,8 0 1,0 10,18 A8,8 0 1,0 10,2',
        fillColor: '#6e6f70',
        fillOpacity: 1,
        scale: 0.6,
        strokeWeight: 0,
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.dropoff_site]: {
        url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(dropoffSiteSvg),
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.staging_site]: {
        url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(stagingSiteSvg),
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.pickup_site]: {
        url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(pickupSiteSvg),
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.both_site]: {
        url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(bothSiteSvg),
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.pickup_site_geo_debug]: {
        url:
          'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(pickupSiteGeodebugSvg),
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.dropoff_site_geo_debug]: {
        url:
          'data:image/svg+xml;charset=utf-8,' +
          encodeURIComponent(dropoffSiteGeodebugSvg),
        anchor: new window.google.maps.Point(9, 9),
      },
      [MarkerType.hidden_marker]: {
        path: 'M 0 0', // minimal path
        fillOpacity: 0,
        strokeOpacity: 0,
        scale: 0,
      },
    } as Record<MarkerType, google.maps.Icon | google.maps.Symbol>;
  }, [isLoaded]);

  const approximateLabelOffset = (rotation: number, label: string) => {
    const isMultiLine = label.includes('\n');
    if (rotation > 315 || rotation <= 45) {
      // N
      return new window.google.maps.Point(16, isMultiLine ? 72 : 56);
    }
    if (rotation > 45 && rotation <= 135) {
      // E
      return new window.google.maps.Point(isMultiLine ? 72 : 56, 16);
    }
    if (rotation > 135 && rotation <= 225) {
      // S
      return new window.google.maps.Point(16, isMultiLine ? -44 : -24);
    }
    // W
    return new window.google.maps.Point(isMultiLine ? -44 : -24, 16);
  };

  const isInteractive =
    marker.type === MarkerType.site ||
    marker.type === MarkerType.pickup_site ||
    marker.type === MarkerType.dropoff_site ||
    marker.type === MarkerType.staging_site ||
    marker.type === MarkerType.both_site ||
    marker.type === MarkerType.truck;
  const isGeoDebugLabelClickable =
    (marker.type === MarkerType.pickup_site_geo_debug ||
      marker.type === MarkerType.dropoff_site_geo_debug) &&
    'onClick' in marker &&
    typeof (marker as { onClick?: () => void }).onClick === 'function';
  const isGeoDebugLabelDraggable =
    (marker.type === MarkerType.pickup_site_geo_debug ||
      marker.type === MarkerType.dropoff_site_geo_debug) &&
    'onDragEnd' in marker &&
    typeof (marker as { onDragEnd?: (p: { lat: number; lng: number }) => void })
      .onDragEnd === 'function';
  const isDraggable =
    (marker.type === MarkerType.site && marker.draggable) || isGeoDebugLabelDraggable;

  const { popoverExists, popoverContent } = useMemo(() => {
    if (
      (marker.type === MarkerType.site ||
        marker.type === MarkerType.pickup_site ||
        marker.type === MarkerType.dropoff_site ||
        marker.type === MarkerType.staging_site ||
        marker.type === MarkerType.both_site) &&
      !marker.draggable &&
      marker.clickPopoverContent
    ) {
      return { popoverExists: true, popoverContent: marker.clickPopoverContent };
    }
    if (marker.type === MarkerType.truck) {
      return {
        popoverExists: true,
        popoverContent:
          popoverVersion === 'hover'
            ? marker.hoverPopoverContent
            : marker.clickPopoverContent,
      };
    }
    return { popoverExists: false, popoverContent: null };
  }, [marker, popoverVersion]);

  const togglePopoverFromMarker = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (anchorEl) {
        setAnchorEl(null);
      } else {
        setAnchorEl(event.domEvent.currentTarget as Element);
      }
    },
    [anchorEl, marker.type, popoverVersion, setPopoverVersion, setAnchorEl],
  );

  return (
    <>
      <Marker
        position={{
          lng: marker.lng,
          lat: marker.lat,
        }}
        zIndex={'zIndex' in marker ? marker.zIndex : undefined}
        animation={google.maps.Animation.DROP}
        label={
          marker.type === MarkerType.truck
            ? {
                text: marker.label,
                color: 'white',
                className: 'marker-label',
              }
            : undefined
        }
        icon={
          markerTypeToIcon?.[marker.type]
            ? {
                ...markerTypeToIcon[marker.type],
                ...(marker.isActive
                  ? {
                      fillColor: marker.type === MarkerType.truck ? '#163786' : '#CB0000',
                    }
                  : {}),
                ...('isDefaultSite' in marker &&
                marker.isDefaultSite &&
                defaultSiteUrlByType[marker.type]
                  ? { url: defaultSiteUrlByType[marker.type] }
                  : {}),
                ...(marker.type === MarkerType.truck
                  ? {
                      rotation: marker.bearing,
                      labelOrigin: approximateLabelOffset(marker.bearing, marker.label),
                    }
                  : {}),
              }
            : undefined
        }
        draggable={isDraggable}
        onMouseDown={isInteractive ? marker.onMouseDown : undefined}
        onMouseUp={isInteractive ? marker.onMouseUp : undefined}
        onMouseOver={
          isInteractive
            ? marker.type === MarkerType.truck
              ? (event) => {
                  togglePopoverFromMarker(event);
                  marker.onMouseOver();
                }
              : marker.onMouseOver
            : undefined
        }
        onMouseOut={isInteractive ? marker.onMouseOut : undefined}
        onDrag={
          isGeoDebugLabelDraggable
            ? (event) => {
                const lat = event.latLng?.lat() ?? 0;
                const lng = event.latLng?.lng() ?? 0;
                (
                  marker as { onDrag?: (p: { lat: number; lng: number }) => void }
                ).onDrag?.({
                  lat,
                  lng,
                });
              }
            : undefined
        }
        onDragEnd={
          isDraggable
            ? (event) => {
                const lat = event.latLng?.lat() ?? 0;
                const lng = event.latLng?.lng() ?? 0;
                if (isGeoDebugLabelDraggable) {
                  (
                    marker as { onDragEnd?: (p: { lat: number; lng: number }) => void }
                  ).onDragEnd?.({ lat, lng });
                } else {
                  marker.onDragEnd?.({ lat, lng });
                }
              }
            : undefined
        }
        onClick={
          popoverExists
            ? togglePopoverFromMarker
            : isGeoDebugLabelClickable
              ? (marker as { onClick?: () => void }).onClick
              : 'onClick' in marker &&
                  typeof (marker as { onClick?: () => void }).onClick === 'function'
                ? (marker as { onClick: () => void }).onClick
                : undefined
        }
      />
      {popoverExists && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            if (marker.type === MarkerType.truck) {
              setPopoverVersion('hover');
            }
          }}
          onClick={
            marker.type === MarkerType.truck && popoverVersion === 'hover'
              ? () => {
                  setPopoverVersion('click');
                }
              : undefined
          }
          onMouseOut={
            marker.type === MarkerType.truck && popoverVersion === 'hover'
              ? () => {
                  setAnchorEl(null);
                }
              : undefined
          }
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {popoverContent}
        </Popover>
      )}
    </>
  );
}
